import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute } from "@tanstack/react-router";

import { PageHero } from "@/components/page-parts";
import {
  FEES,
  FIRM,
  FIRM_ADDRESS,
  FIRM_POSTAL_ADDRESS,
  MICHAEL_PHONE,
  PENINSULA_ADDRESS,
  PENINSULA_OFFICE,
} from "@/lib/firm";

/**
 * Internal playbook page — noindexed and unlinked from the public navigation.
 * Copy-ready citation pack for the Scarborough place of practice. Name, phone,
 * email, hours, categories and description are identical to the Golden Beach
 * pack; only the street address and the areas-served field differ, because a
 * directory record must show the premises it actually belongs to.
 */
export const Route = createFileRoute("/directory-listings-scarborough")({
  head: () => ({
    meta: [
      { title: fitTitle(`Scarborough directory pack (internal) | ${FIRM.name}`) },
      {
        name: "description",
        content:
          fitDescription("Internal pack: copy-ready local directory listings for the Coastside Law Scarborough office, with matching NAP, tagged website URLs and tagged enquiry links."),
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Coastside Law — Scarborough directory pack" },
      { property: "og:description", content: "Internal reference for Coastside Law's Scarborough office listing details and links." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ScarboroughDirectoryPack,
});

const BASE = "https://coastsidelaw.com.au";

const CATEGORIES =
  "Primary: Conveyancer. Secondary: Law firm, Estate planning attorney, Elder law attorney, Real estate attorney.";

const AREAS =
  "Scarborough, Redcliffe, Margate, Woody Point, Clontarf, Newport, Kippa-Ring, Rothwell, North Lakes, Mango Hill, Deception Bay, Burpengary, Caboolture, wider Moreton Bay";

const DESCRIPTION = `Coastside Law is a Queensland law practice at ${PENINSULA_ADDRESS}, acting for buyers and sellers across Scarborough and the Redcliffe Peninsula. Fixed-fee residential conveyancing — ${FEES.sale} incl. GST for a sale and ${FEES.purchase} incl. GST for a purchase — with a free pre-signing contract review. Every file is handled by a solicitor, not a conveyancing clerk, so a matter that turns into a deceased estate, family transfer or contract dispute stays with the same firm. We also prepare wills, enduring powers of attorney and probate applications. ${FIRM.membership}.`;

/** Every directory that takes a second premises record, plus its utm_source. */
const DIRECTORIES: ReadonlyArray<{ name: string; where: string; source: string; note: string }> = [
  {
    name: "Google Business Profile",
    where: "business.google.com",
    source: "gbp-scarborough",
    note: "A second profile is only allowed if Scarborough is staffed during the stated hours. If it is appointment-only with nobody there, do not create it — add the Peninsula suburbs as service areas on the Golden Beach profile instead.",
  },
  {
    name: "Bing Places for Business",
    where: "bingplaces.com",
    source: "bing-places",
    note: "Add as a second location under the same business. Bing feeds Copilot and several AI assistants.",
  },
  {
    name: "Apple Business Connect",
    where: "businessconnect.apple.com",
    source: "apple-maps",
    note: "Second place card. Confirm the pin drops on Fortune Street, not the Redcliffe CBD.",
  },
  {
    name: "Queensland Law Society find-a-solicitor",
    where: "qls.com.au",
    source: "qls",
    note: "List Golden Beach as the principal office and Scarborough as an additional place of practice on the same firm record — never as a separate firm.",
  },
  {
    name: "Yellow Pages Australia",
    where: "yellowpages.com.au",
    source: "yellowpages",
    note: "Free second-location listing. Suburbs go in areas served, never in the business name.",
  },
  {
    name: "True Local",
    where: "truelocal.com.au",
    source: "truelocal",
    note: "Second branch record. Keep the description identical to Golden Beach apart from the address line.",
  },
  {
    name: "Hotfrog Australia",
    where: "hotfrog.com.au",
    source: "hotfrog",
    note: "Free and indexed quickly. One paragraph plus the tagged link.",
  },
  {
    name: "Localsearch",
    where: "localsearch.com.au",
    source: "localsearch",
    note: "Keep the phone identical to the Golden Beach record or the two premises split into unrelated businesses.",
  },
  {
    name: "Cylex Australia",
    where: "cylex.com.au",
    source: "cylex",
    note: "Low-effort citation that reinforces NAP consistency for the second premises.",
  },
  {
    name: "StartLocal",
    where: "startlocal.com.au",
    source: "startlocal",
    note: "Useful for long-tail Redcliffe Peninsula suburb queries.",
  },
  {
    name: "Moreton Bay / Redcliffe Peninsula Chamber of Commerce",
    where: "moretonbaychamber.com.au",
    source: "moretonbay-chamber",
    note: "Member profile. This is the strongest local link on the Peninsula side.",
  },
  {
    name: "Moreton Bay City Council business directory",
    where: "moretonbay.qld.gov.au business listings",
    source: "mbrc-directory",
    note: "Scarborough address, Peninsula suburbs listed as areas served.",
  },
  {
    name: "LinkedIn company page",
    where: "linkedin.com/company",
    source: "linkedin",
    note: "Add Scarborough as a second location on the existing company page.",
  },
  {
    name: "Facebook business page",
    where: "facebook.com",
    source: "facebook",
    note: "One page, two locations. Do not create a second page — it splits reviews.",
  },
];

function websiteUrl(source: string) {
  return `${BASE}/scarborough?utm_source=${source}&utm_medium=referral&utm_campaign=directory-scarborough`;
}

function enquiryLinkUrl(source: string) {
  return `${websiteUrl(source)}&utm_content=enquiry#enquiry`;
}

const FIELDS: ReadonlyArray<[string, string]> = [
  ["Business name", FIRM.name],
  ["Street address", PENINSULA_ADDRESS],
  ["Postal address", FIRM_POSTAL_ADDRESS],
  ["Phone", FIRM.phone],
  ["Second phone", `${MICHAEL_PHONE} (Michael Klein, Peninsula direct line)`],
  ["Contact", "Michael Klein"],
  ["Email", FIRM.email],
  ["Hours", FIRM.hours],
  ["Categories", CATEGORIES],
  ["Areas served", AREAS],
  ["Website", `${BASE}/scarborough`],
];

const RULES: readonly string[] = [
  `Business name is exactly "${FIRM.name}" — no suburb, no service words. "Coastside Law Scarborough" or "Coastside Law Conveyancing Redcliffe" breaks NAP matching against the Golden Beach record and risks suppression on both.`,
  `Phone stays ${FIRM.phone} in the primary phone field on every citation, the same number as Golden Beach. Michael's ${MICHAEL_PHONE} goes in the secondary or mobile field only. Two different primary numbers is what makes Google treat two premises as two unrelated businesses.`,
  `The address is ${PENINSULA_ADDRESS} — the real premises. It is the one field that legitimately differs from the Golden Beach record; never paste ${FIRM_ADDRESS} onto a Scarborough listing or vice versa.`,
  "One record per premises. Peninsula suburbs go in the areas-served field on this record — they never get an address of their own.",
  "Submit the Golden Beach master record first and let it settle, then this one. Two brand-new premises appearing on the same day looks like a bulk upload.",
  "Use the tagged website URL in the website field and the tagged enquiry URL wherever a directory offers a second link, quote button or contact link.",
];

export default function ScarboroughDirectoryPack() {
  return (
    <>
      <PageHero
        eyebrow="Internal"
        title="Scarborough directory pack"
        lead="Copy-ready citations for the Peninsula premises. Same name, phone, email, hours, categories and description as the Golden Beach pack — only the street address and the areas-served field change."
      />

      <div className="mx-auto max-w-4xl px-6 pt-16">
        <p className="rounded-sm border border-primary/40 bg-primary/5 p-4 text-sm leading-relaxed text-muted-foreground">
          One caveat before you start. A directory record must show the premises it belongs to, so
          the Scarborough address stays {PENINSULA_ADDRESS} — it cannot carry the Golden Beach
          street address. Everything else in the NAP is deliberately identical, and that is what
          ties the two premises to one entity for a knowledge panel. Check the Google Business
          Profile name reads exactly <strong className="text-foreground">{FIRM.name}</strong>{" "}
          before submitting anything here.
        </p>

        <section className="mt-10 rounded-sm border border-border bg-card p-6">
          <h2 className="font-serif text-2xl text-foreground">
            {PENINSULA_OFFICE.suburb} — additional place of practice ({PENINSULA_OFFICE.note})
          </h2>
          <dl className="mt-4 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-[10rem_1fr]">
            {FIELDS.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="font-semibold text-foreground">{k}</dt>
                <dd className="break-words text-muted-foreground">{v}</dd>
              </div>
            ))}
          </dl>

          <p className="mt-6 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Description — paste as one paragraph
          </p>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{DESCRIPTION}</p>
        </section>
      </div>

      <div className="mx-auto max-w-4xl px-6 py-16 sm:py-20">
        <h2 className="font-serif text-3xl text-foreground">Before you submit anything</h2>
        <ul className="mt-6 space-y-3">
          {RULES.map((r) => (
            <li
              key={r}
              className="rounded-sm border border-border bg-card px-4 py-3 text-sm leading-relaxed text-muted-foreground"
            >
              {r}
            </li>
          ))}
        </ul>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Where to submit this record</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          Every link is tagged, so a Peninsula lead from Hotfrog is separable from a Caloundra lead
          in{" "}
          <a href="/analytics" className="font-semibold text-primary underline">
            /analytics
          </a>
          . Paste the URLs in full — the tracking lives in the query string, and the enquiry link
          jumps straight to the form and emails both Erin and Michael.
        </p>

        <ul className="mt-6 grid gap-3">
          {DIRECTORIES.map((d) => (
            <li
              key={d.name}
              className="rounded-sm border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
            >
              <p className="font-semibold text-foreground">{d.name}</p>
              <p className="mt-1">
                {d.where} — {d.note}
              </p>
              <p className="mt-2 text-xs font-semibold uppercase tracking-[0.12em]">Website link</p>
              <code className="mt-1 block break-all font-mono text-xs">
                {websiteUrl(d.source)}
              </code>
              <p className="mt-2 text-xs font-semibold uppercase tracking-[0.12em]">
                Enquiry / quote link
              </p>
              <code className="mt-1 block break-all font-mono text-xs">
                {enquiryLinkUrl(d.source)}
              </code>
            </li>
          ))}
        </ul>

        <p className="mt-16 rounded-sm border border-border bg-card p-5 text-sm leading-relaxed text-muted-foreground">
          The Golden Beach master record and the suburb-by-suburb pack sit on the{" "}
          <a href="/directory-listings" className="font-semibold text-primary underline">
            main directory page
          </a>
          , and the profile side sits on the{" "}
          <a href="/google-business-profile" className="font-semibold text-primary underline">
            profile playbook
          </a>
          .
        </p>
      </div>
    </>
  );
}
